// Service detail page — duration picker, partner block, Book Now CTA.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import BookingSheet from '../components/services/BookingSheet';
import ProductReviews from '../components/ProductReviews';

const fmtINR = (n) =>
  n == null
    ? null
    : new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(n);

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, me] = await Promise.all([
          axiosInstance.get(`/services/${id}`),
          axiosInstance.get('/auth/me').catch(() => ({ data: null })),
        ]);
        setService(s.data);
        setUser(me.data);
        if (s.data.variants?.length) setSelectedVariant(s.data.variants[0]);
      } catch (e) {
        toast.error('Failed to load service');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]" />
      </div>
    );
  }
  if (!service) return null;

  const isOnRequest = service.combo_only || selectedVariant?.price == null;
  const ctaPrice = selectedVariant?.price;

  return (
    <div
      className="min-h-screen bg-[#F9FAFB] text-[#2B3A67] pb-40"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <header className="sticky top-0 z-30 bg-[#F9FAFB]/90 backdrop-blur-md border-b border-gray-100 pt-safe">
        <div className="flex items-center justify-between px-6 py-4">
          <button
            data-testid="back-button"
            onClick={() => navigate('/shop?tab=services')}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
          >
            <i className="ph-bold ph-arrow-left text-xl" />
          </button>
          <h1 className="font-extrabold text-lg" data-testid="service-detail-title">
            {service.name}
          </h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="px-6 pt-6">
        {service.images?.[0] && (
          <section className="bg-white rounded-3xl shadow-lg p-4 border border-gray-100 mb-6">
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img
                data-testid="service-detail-image"
                src={service.images[0]}
                alt={service.name}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
          </section>
        )}

        {/* Partner badge */}
        {service.partner_brand && (
          <div className="flex items-center gap-3 bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 mb-4">
            {service.partner_logo && (
              <img
                src={service.partner_logo}
                alt={service.partner_brand}
                className="w-10 h-10 rounded-full object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <div className="flex-1">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Partner
              </p>
              <p className="font-extrabold text-sm" data-testid="service-detail-partner">
                {service.partner_brand}
              </p>
            </div>
            {service.partner_url && (
              <a
                href={service.partner_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] font-bold text-[#E54562]"
                data-testid="service-detail-partner-link"
              >
                Visit ↗
              </a>
            )}
          </div>
        )}

        <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">
          {service.service_category}
        </p>
        <h2 className="text-2xl font-extrabold mb-3">{service.name}</h2>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed" data-testid="service-detail-description">
          {service.description}
        </p>

        {/* Duration variants */}
        <h3 className="text-sm font-extrabold mb-3">Choose Duration</h3>
        <div className="space-y-2 mb-6">
          {service.variants.map((v) => {
            const active = selectedVariant?.id === v.id;
            return (
              <button
                key={v.id}
                data-testid={`service-variant-${v.id}`}
                onClick={() => setSelectedVariant(v)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border-2 ${
                  active
                    ? 'border-[#2B3A67] bg-[#2B3A67]/5'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="text-left">
                  <p className="font-extrabold text-sm">{v.label}</p>
                  <p className="text-xs text-gray-400">{v.duration_minutes} minutes</p>
                </div>
                <div className="font-black text-[#E54562]">
                  {v.price == null ? 'On request' : fmtINR(v.price)}
                </div>
              </button>
            );
          })}
        </div>

        {/* City availability */}
        {service.available_cities?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
              Available In
            </h3>
            <div className="flex flex-wrap gap-2">
              {service.available_cities.map((c) => (
                <span
                  key={c}
                  className="text-xs font-bold px-3 py-1.5 rounded-full bg-white border border-gray-200"
                >
                  📍 {c}
                </span>
              ))}
            </div>
          </div>
        )}

        <ProductReviews productId={service.id} />
      </main>

      {/* Sticky CTA */}
      <div className="fixed bottom-6 left-0 right-0 px-6 pointer-events-none">
        <button
          data-testid="service-book-now"
          onClick={() => setSheetOpen(true)}
          disabled={!selectedVariant}
          className="pointer-events-auto w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg disabled:opacity-50"
        >
          {isOnRequest
            ? 'Send Enquiry'
            : `Book Now${ctaPrice ? ' · ' + fmtINR(ctaPrice) : ''}`}
        </button>
      </div>

      <BookingSheet
        open={sheetOpen}
        service={service}
        variant={selectedVariant}
        user={user}
        onClose={() => setSheetOpen(false)}
        onBooked={() => {
          setSheetOpen(false);
          navigate('/profile');
        }}
      />
    </div>
  );
}
